import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import InfoCard from "@/components/ui/info-card";
import Chip from "@/components/ui/chip";
import TicketCategoryCell from "@/features/support-tickets/components/TicketCategoryCell";
import {
  DocumentModal,
  Pending,
  Tick,
  Eye as EyeIcon,
} from "@/components/icons";
import type { FilterField, SortOption, TableColumn } from "@/types/component";
import type {
  SupportTicketListItem,
  SupportTicketSortByValue,
  TicketStatusBackend,
} from "../types/support-tickets.types";
import {
  SupportTicketSortBy,
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
} from "../types/support-tickets.types";
import { supportTicketsApi } from "../services/support-tickets.api";
import SupportTicketDetailDrawer from "../components/SupportTicketDetailDrawer";
import NewClientSupportTicketModal from "@/features/client-support-tickets/components/NewClientSupportTicketModal";
import Toast from "@/components/ui/toast";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./SupportTicketsPage.module.scss";
function formatTicketDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Seçilen günün başı (00:00:00) → ISO string */
function toStartOfDayISO(d: Date): string {
  const start = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    0,
    0,
    0,
    0,
  );
  return start.toISOString();
}

/** Seçilen günün sonu (23:59:59.999) → ISO string */
function toEndOfDayISO(d: Date): string {
  const end = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  );
  return end.toISOString();
}

/** Tarih aralığı filtresi [Date, Date] → API: dateFrom gün başı, dateTo gün sonu (ISO) */
function parseDateRangeValue(value: unknown): {
  dateFrom?: string;
  dateTo?: string;
} {
  if (!value || !Array.isArray(value)) return {};
  const [start, end] = value as [Date | null, Date | null];
  return {
    dateFrom:
      start && !Number.isNaN(start.getTime())
        ? toStartOfDayISO(start)
        : undefined,
    dateTo:
      end && !Number.isNaN(end.getTime()) ? toEndOfDayISO(end) : undefined,
  };
}

const SupportTicketsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  // Eğitim Sorumlusu gibi SUPPORT_TICKETS.CREATE yetkisine sahip olup admin
  // menüsünden bu sayfayı gören roller için "Yeni Talep" butonu burada da
  // görünmeli. Kullandığımız modal /api/client/support-tickets endpoint'ine
  // POST atar; endpoint yalnızca page-access guard'ıyla korunuyor, yani
  // isStaff fark etmeksizin authenticated admin kullanıcısı için de çalışır.
  const canCreateTicket = hasPermission(PERMISSIONS.SUPPORT_TICKETS_CREATE);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [toastState, setToastState] = useState<{
    variant: "success" | "fail";
    title?: string;
    message: string;
  } | null>(null);

  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState({
    totalCount: 0,
    pendingCount: 0,
    resolvedCount: 0,
  });
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [sortValue, setSortValue] = useState<SupportTicketSortByValue>(
    SupportTicketSortBy.CREATED_AT_DESC,
  );
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // Search debounce (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTickets = useCallback(async () => {
    const dateRange = parseDateRangeValue(filterValues.dateRange);
    setIsLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        supportTicketsApi.getAll({
          page: currentPage,
          limit: Math.min(pageSize, 100),
          search: debouncedSearch.trim() || undefined,
          status: (() => {
            const s = filterValues.status;
            if (s == null || s === "") return undefined;
            const num = Number(s);
            return num === 2 || num === 3
              ? (num as TicketStatusBackend)
              : undefined;
          })(),
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          sortBy: sortValue,
        }),
        supportTicketsApi.getStats(),
      ]);
      setTickets(listRes.data);
      setTotalRecords(listRes.total);
      setStats(statsRes);
    } catch (error) {
      console.error("Error fetching support tickets:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, filterValues, sortValue]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const handleSortChange = (option: SortOption) => {
    if (option?.value) setSortValue(option.value as SupportTicketSortByValue);
    setCurrentPage(1);
  };

  const sortOptions: SortOption[] = [
    {
      label: t("supportTickets.sort.answeredFirst"),
      value: SupportTicketSortBy.ANSWERED_FIRST,
    },
    {
      label: t("supportTickets.sort.pendingFirst"),
      value: SupportTicketSortBy.PENDING_FIRST,
    },
    {
      label: t("supportTickets.sort.createdAtDesc"),
      value: SupportTicketSortBy.CREATED_AT_DESC,
    },
    {
      label: t("supportTickets.sort.createdAtAsc"),
      value: SupportTicketSortBy.CREATED_AT_ASC,
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("supportTickets.filters.status"),
      type: "dropdown",
      placeholder: t("supportTickets.filters.statusPlaceholder"),
      options: [
        { label: SUPPORT_TICKET_STATUS_LABELS_TR[2], value: "2" },
        { label: SUPPORT_TICKET_STATUS_LABELS_TR[3], value: "3" },
      ],
    },
    {
      name: "dateRange",
      label: t("supportTickets.filters.dateRange"),
      type: "daterange",
      placeholder: t("supportTickets.filters.datePlaceholder"),
    },
  ];

  const handleViewDetail = (row: SupportTicketListItem) => {
    setSelectedTicketId(row.id);
    setDetailDrawerOpen(true);
  };

  const columns: TableColumn<SupportTicketListItem>[] = [
    {
      header: t("supportTickets.columns.ticketNo"),
      accessor: "requestNo",
      className: "ticket-no-column",
    },
    {
      header: t("supportTickets.columns.personnelName"),
      accessor: "personnelName",
      className: "personnel-name-column",
    },
    {
      header: t("supportTickets.columns.createdAt"),
      accessor: (row) => formatTicketDate(row.createdAt),
      className: "created-at-column",
    },
    {
      header: t("supportTickets.columns.education"),
      accessor: (row) => row.education?.name ?? "—",
      className: "education-column",
    },
    {
      header: t("supportTicketCategory.column"),
      accessor: (row) => (
        <TicketCategoryCell category={row.category} approvalStatus={row.approvalStatus} />
      ),
      className: "category-column",
    },
    {
      header: t("supportTickets.columns.status"),
      accessor: (row) => (
        <Chip
          type={SUPPORT_TICKET_STATUS_CHIP_TYPE[row.status]}
          size="small"
          variant="filled"
        >
          {SUPPORT_TICKET_STATUS_LABELS_TR[row.status]}
        </Chip>
      ),
      className: "status-column",
    },
  ];

  const renderActions = (row: SupportTicketListItem) => (
    <button
      type="button"
      className={styles.viewButton}
      onClick={(e) => {
        e.stopPropagation();
        handleViewDetail(row);
      }}
      aria-label={t("common.view")}
    >
      <EyeIcon width={20} height={20} />
    </button>
  );

  return (
    <DashboardLayout>
      <Header title={t("supportTickets.title")} />

      <div className={styles.infoCardsGrid}>
        <InfoCard
          title={t("supportTickets.stats.total")}
          value={stats.totalCount}
          icon={<DocumentModal width={24} height={24} color="#fff" />}
        />
        <InfoCard
          title={t("supportTickets.stats.pending")}
          value={stats.pendingCount}
          icon={<Pending width={24} height={24} color="#fff" />}
        />
        <InfoCard
          title={t("supportTickets.stats.resolved")}
          value={stats.resolvedCount}
          icon={<Tick width={24} height={24} color="#fff" />}
        />
      </div>

      <PageHeader
        searchPlaceholder={t("supportTickets.searchPlaceholder")}
        searchValue={searchTerm}
        searchMaxLength={200}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={sortValue}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreateTicket ? () => setNewTicketOpen(true) : undefined}
        addButtonLabel={
          canCreateTicket
            ? t("clientSupportTickets.newTicket.button")
            : undefined
        }
      />

      <Table
        columns={columns}
        data={tickets}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        emptyMessage={t("supportTickets.noData")}
      />

      <SupportTicketDetailDrawer
        isOpen={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedTicketId(null);
        }}
        ticketId={selectedTicketId}
        onTicketUpdated={fetchTickets}
      />

      {canCreateTicket && (
        <NewClientSupportTicketModal
          isOpen={newTicketOpen}
          onClose={() => setNewTicketOpen(false)}
          onSuccess={() => {
            setNewTicketOpen(false);
            setToastState({
              variant: "success",
              title: t("clientSupportTickets.newTicket.toastTitle"),
              message: t("clientSupportTickets.newTicket.toastSuccess"),
            });
            fetchTickets();
          }}
        />
      )}

      {toastState && (
        <Toast
          variant={toastState.variant}
          title={toastState.title}
          message={toastState.message}
          onClose={() => setToastState(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default SupportTicketsPage;
